// US-aware geocoder that wraps Nominatim with:
//   1. Structured query (street/city/state/postalcode/country) — accurate
//   2. countrycodes=us — won't drift to Mexico/Canada/etc.
//   3. addressdetails=1 + state-match validation — rejects results where the
//      returned state doesn't agree with the input state. This is what stops a
//      Tennessee address from being silently matched to "Provo, UT".
//
// Returns { lat, lon, source } on a confident match, or null when nothing
// validates. Callers should treat null as "keep existing coords, don't update".

const STATE_ABBR_TO_NAME = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan",
  MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee",
  TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
}

const STATE_NAME_TO_ABBR = Object.fromEntries(
  Object.entries(STATE_ABBR_TO_NAME).map(([abbr, name]) => [name.toLowerCase(), abbr])
)

function toAbbr(state) {
  if (!state) return ""
  const trimmed = state.trim()
  if (trimmed.length === 2) return trimmed.toUpperCase()
  return STATE_NAME_TO_ABBR[trimmed.toLowerCase()] || trimmed.toUpperCase()
}

function statesMatch(resultState, inputState) {
  if (!inputState) return true // nothing to validate against
  if (!resultState) return false
  return toAbbr(resultState) === toAbbr(inputState)
}

async function nominatimSearch(params) {
  const url = `https://nominatim.openstreetmap.org/search?${params}`
  const res = await fetch(url)
  if (!res.ok) return []
  const data = await res.json()
  return Array.isArray(data) ? data : []
}

function pickValidatedResult(results, targetState, source) {
  for (const r of results) {
    const rState = r.address?.state || ""
    if (statesMatch(rState, targetState)) {
      return { lat: parseFloat(r.lat), lon: parseFloat(r.lon), source }
    }
  }
  return null
}

export async function geocodeAddress({ address, address2, city, state, zip } = {}) {
  const streetParts = [address, address2].map((s) => s && s.trim()).filter(Boolean).join(" ")
  const targetState = state ? state.trim() : ""

  // Strategy 1: structured query, US-constrained
  if (streetParts || (city && state)) {
    const p = new URLSearchParams({
      format: "json",
      addressdetails: "1",
      countrycodes: "us",
      limit: "5",
      country: "USA",
    })
    if (streetParts) p.set("street", streetParts)
    if (city) p.set("city", city)
    if (state) p.set("state", state)
    if (zip) p.set("postalcode", zip)
    const hit = pickValidatedResult(await nominatimSearch(p.toString()), targetState, "structured")
    if (hit) return hit
  }

  // Strategy 2: free-form query, US-constrained, with state validation
  const fullAddress = [streetParts, city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")
    .replace(/\s+/g, " ")
    .trim()
  if (fullAddress && fullAddress !== ",") {
    const p = new URLSearchParams({
      format: "json",
      addressdetails: "1",
      countrycodes: "us",
      limit: "5",
      q: fullAddress,
    })
    const hit = pickValidatedResult(await nominatimSearch(p.toString()), targetState, "freeform-us")
    if (hit) return hit
  }

  // Strategy 3: city/state/zip only — last resort, still state-validated
  const broader = [city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")
    .replace(/\s+/g, " ")
    .trim()
  if (broader && broader !== ",") {
    const p = new URLSearchParams({
      format: "json",
      addressdetails: "1",
      countrycodes: "us",
      limit: "5",
      q: broader,
    })
    const hit = pickValidatedResult(await nominatimSearch(p.toString()), targetState, "city-state-zip")
    if (hit) return hit
  }

  return null
}
